import { useEffect, useState, useRef } from "react";

import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext.jsx";
import { useApp, getEndpoint } from "../../context/AppContext.jsx";
import { Board } from "../../component/drag/Board.jsx";
import { CardImage, CardTextGroup } from "../../component/mtg/CardSymbol.jsx";
import { isCardTransform } from "../../component/mtg/CardSymbol.jsx";
import { MyButton, MyEditableText, MySelectBoxLabel } from "../../component/ui/UI.jsx";
import { useSave } from "../../context/SaveContext.jsx";
import { createColumn } from "../../context/SaveData.js";
import { useAppNavigate } from "../Route.jsx";
import { restPostJson } from "@MyLib/System/Rest.js";
import { exportMTGADeck, importMTGADeck } from "../../component/mtg/MTGA.jsx";

const DeckInfoCanvas = ({deck}) =>
{
	const { t } = useTranslation();
	return (
		<div
			className="card p-0 h-100 flex-grow-0 p-1"
			style={{ flex: "0 0 240px" }}
		>
			<table className="table table-sm table-borderless text-nowrap text-muted"
				style={{
					width: '100%',
					tableLayout: "fixed"
				}}
			>
				<colgroup>
					<col />
					<col style={{ width: "40px" }} />
				</colgroup>
				<tbody>
					{deck.columns.map((item, index) => (
					<tr key={index}>
						<th className="text-muted text-truncate">{item.name}</th>
						<th className="text-muted">{item.cards.reduce((sum, item) => sum + item.num, 0)}</th>
					</tr>
					))}
					<tr>
						<th>{t("Common_Total")}</th>
						<th>{deck.columns.reduce((sum, col) => {
							if (col.name?.startsWith("*")) {
								return sum;
							}
							return sum + col.cards.reduce((sum, item) => sum + (item.num), 0)
						}, 0)}
						</th>
					</tr>
				</tbody>
			</table>
		</div>
	);
};

const DeckBuilderCardCanvas = ({card, onClose, nextCard}) =>
{
	const { t } = useTranslation();
	const {filters} = useSave();
	const {addKeepCards} = useApp();

	const [transform, setTransform] = useState("front");
	const [frontCard, setFrontCard] = useState();
	const [backCard, setBackCard] = useState();

	useEffect(() => {
		if (card)
		{
			(async() => {
				let front = null;
				{
					try {
						const query = new URLSearchParams();
						query.append("uuid", card.uuid);
						const res = await fetch(`${getEndpoint()}/mtg-card?${query.toString()}`);
						const data = await res.json();
						setTransform("front");
						front = data;
						setFrontCard(data);
						setBackCard(null);
					} catch (err) {
						console.error(err);
					}
				}
				if (front && front.otherFaceIds)
				{
					try {
						const query = new URLSearchParams();
						query.append("uuid", front.otherFaceIds);
						const res = await fetch(`${getEndpoint()}/mtg-card?${query.toString()}`);
						const data = await res.json();
						setBackCard(data);
					} catch (err) {
						console.error(err);
					}
				}
			})();
		}
	}, [card]);

	useEffect(() => {
	}, [frontCard, backCard, transform]);

	const onTransform = () => {
		if (transform == "front" && backCard)
		{
			setTransform("back");
		}
		else if (transform =="back")
		{
			setTransform("front");
		}
	};

	if (!frontCard)
	{
		return (<div />);
	}
	return (
		<div
			className="card p-0 h-100"
			style={{
				width: '400px',
				flexShrink: 0
			}}
		>
			<div className="p-1 d-flex gap-1">
				<div className="d-flex justify-content-between align-items-center flex-grow-1">
					<div className='d-flex gap-1'>
						<MyButton onClick={() => {nextCard(-1);}} text={t("Common_Previous")} />
						<MyButton onClick={(e) => {nextCard(+1);}} text={t("Common_Next")} />
					</div>
					<div className='d-flex gap-1'>
						<MyButton
							text={t("AddCard")}
							onClick={()=>{
								addKeepCards([card]);
							}}
						/>
						<MyButton text={t("Common_Close")} onClick={onClose} />
					</div>
				</div>
			</div>
			<div
				className="p-1"
				style={{overflowY: "auto", overflowX: "hidden"}}
			>
				<div>
					<CardImage
						card={(transform=="front") ? frontCard : backCard}
						face={transform}
						uuid={(transform=="front") ? frontCard.uuid : backCard.uuid}
						language={filters.language}
						onClose={onClose}
						onTransform={isCardTransform(frontCard) ? onTransform : null}
					/>
				</div>
				<div>
					<CardTextGroup card={frontCard} other={backCard} transform={transform} />
				</div>
			</div>
		</div>
	);
};

const CardDisplaySize = () => {
	const {cardDisplaySize, setCardDisplaySize} = useSave();
	return (
		<MySelectBoxLabel
			itemList={[
				{value: "sm", text: "sm"},
				{value: "md", text: "md"},
				{value: "lg", text: "lg"}
			]}
			value={cardDisplaySize}
			onChange={(e) => setCardDisplaySize(e.target.value)}
		/>
	);
}

const useCardSelect = () => {
	const [selectCard, setSelectCard] = useState(null);
	const [showCard, setShowCard] = useState(null);

	return {
		selectCard,
		setSelectCard,
		showCard,
		setShowCard
	};
};

export const DeckBuilder = () =>
{
	const { t } = useTranslation();
	const {gotoMyDeck} = useAppNavigate();
	const { user } = useAuth();
	const {setBuildCardDatas, buildCardDatas, setYesNoModal, keepCards, setKeepCards, addKeepCards} = useApp();
	const {saveDeck, download, open, buildDeck, setBuildDeck, deckList, saveDeckList, addBuildCards, filters} = useSave();

	const cardSelector = useCardSelect();
	const [showDeckInfo, setShowDeckInfo] = useState(false);

	const headerRef = useRef(null);
	const [headerHeight, setHeaderHeight] = useState(0);

	useEffect(() => {
		if (buildDeck === null) {
			gotoMyDeck();
			return;
		}

		const resize = () => {
			if (headerRef.current) {
				setHeaderHeight(headerRef.current.offsetHeight);
			}
		};

		resize();
		window.addEventListener("resize", resize);
		return () => window.removeEventListener("resize", resize);
	}, []);

	const updateCardData = async (deck, cache=false) => {
		if (deck == null) {
			return;
		}
		try {
			let ids = deck.columns.flatMap(col => col.cards.map(card => ({uuid: card.uuid, language: card.language})));
			if (cache)
			{
				const existingIds = buildCardDatas.map(item => item.uuid);
				ids = ids.filter(id => !existingIds.includes(id.uuid));
			}
			if (ids.length == 0) {
				return;
			}
			const data = await restPostJson(`${getEndpoint()}/mtg-cards-foregin`, {
				ids: ids
			});
			setBuildCardDatas([...buildCardDatas, ...data]);
		} catch (err) {
			console.error(err);
		}
	};

	useEffect(() => {
		(async() => {
			await updateCardData(buildDeck);
		})();
	}, []);

	const setDeckName = async (newName) => {
		if (newName == "" || newName == buildDeck.name) {
			return;
		}
		const newDeck = { ...buildDeck, name: newName };
		const newDeckInfo = deckList.map(item => {
			if (item.id === newDeck.id) {
				return { ...item, name: newDeck.name };
			}
			return item;
		});
		await saveDeckList(newDeckInfo);
		await saveDeck(newDeck);
	};

	const nextCard = (v) => {
		const allCards = buildDeck.columns.flatMap(col => col.cards);
		const currentIndex = allCards.findIndex(card => card.id === cardSelector.showCard.id);
		const nextIndex = (currentIndex + v + allCards.length) % allCards.length;
		cardSelector.setShowCard(allCards[nextIndex]);
	};

	const download2 = (deck) => {
		exportMTGADeck(buildDeck, buildCardDatas);
	};

	if (buildDeck == null) {
		return <div />
	}
	return (
		<div
			className="container-fluid h-100 p-0"
			style={{
				overflow: "hidden"
			}}
		>
			{/* Header */}
			<div
				ref={headerRef}
				className="row align-items-center m-0"
			>
				<div className="col-12 col-md-auto d-flex align-items-center p-1">
					<MyEditableText
						value={buildDeck.name}
						displayClass="fs-3 fw-bold"
						inputClass="form-control form-control-lg"
						onChange={(name)=>{setDeckName(name)}}
					/>
				</div>
				<div className="col-12 col-md d-flex justify-content-start justify-content-md-end gap-1 p-1 flex-wrap">
					<MyButton
						onClick={()=>{
							setKeepCards([]);
						}}
						disabled={keepCards.length==0}
						text={t("ClearCard")}
					/>
					<MyButton onClick={()=>setShowDeckInfo(!showDeckInfo)} text={t("Common_Information")} />
					<CardDisplaySize />
					<MyButton onClick={() => {saveDeck(buildDeck)}} text={t("Common_Upload")} disabled={!user}/>
					<MyButton onClick={() => {download(buildDeck)}} text={t("Common_Download")} />
					<MyButton
						onClick={async () => {
							try {
								const data = await open();
								if (data) {
									setBuildDeck({
										...buildDeck,
										columns: [...buildDeck.columns, ...data.columns]
									});
								}
							} catch (error) {
								console.error(error.message);
							}
						}}
						text={t("Common_Open")}
					/>
					<MyButton
						onClick={async () => {
							const mtgaCards = await importMTGADeck(filters.language);
							const newColumns = [...buildDeck.columns, {...createColumn(), cards: mtgaCards}];
							const newData = { ...buildDeck, columns: newColumns };
							setBuildDeck(newData);
							await updateCardData(newData, true);
						}}
						text={t("MTGA_Import")}
					/>
					<MyButton onClick={() => {download2(buildDeck)}} text={t("MTGA_Export")} />
				</div>
			</div>
			{/* Main Area */}
			<div
				className="d-flex px-1 pb-1 gap-1"
				style={{
					height: `calc(100% - ${headerHeight}px)`,
					overflowX: "hidden"
				}}
			>
				{showDeckInfo &&
					<DeckInfoCanvas deck={buildDeck} />
				}
				{cardSelector.showCard &&
					<DeckBuilderCardCanvas card={cardSelector.showCard} onClose={()=>cardSelector.setShowCard(null)} nextCard={nextCard} />
				}
				<div
        className="h-100 flex-grow-1"
        style={{
            minWidth: 0,
            overflow: "hidden",
        }}>
					<Board
						addColumn={()=>{
							const newColumns = [...buildDeck.columns, createColumn()];
							const newData = { ...buildDeck, columns: newColumns };
							setBuildDeck(newData);
						}}
						deleteColumn={(index)=>{
							setYesNoModal({
								title: t("DeleteGroup"),
								body: <div>{t("DeleteGroupAsk")}</div>,
								onClose: () => {
									setYesNoModal(null);
								},
								onSubmit: async () => {
									const newColumns = buildDeck.columns.filter((_, i) => i !== index);
									const newData = { ...buildDeck, columns: newColumns };
									setBuildDeck(newData);
								},
							});
						}}
						copyColumn={async (index) => {
							/*
							try {
								const json = JSON.stringify({cards: buildDeck.columns[index].cards}, null, '\t');
								await navigator.clipboard.writeText(json);
							} catch (err) {
							}
							*/
							addKeepCards(buildDeck.columns[index].cards);
						}}
						pasteColumn={async (index) => {
							/*
							try {
								const text = await navigator.clipboard.readText();
								const parsedData = JSON.parse(text);
								if ('cards' in parsedData)
								{
									const cards = parsedData.cards;
									if (Array.isArray(cards))
									{
										if (cards.every(item =>
											typeof item.id === 'string' &&
											typeof item.uuid === 'string' &&
											typeof item.language === 'string' &&
											typeof item.num === 'number'
										)) {
											const currentDeck = addBuildCards(cards, index);
											await updateCardData(currentDeck);
										}
									}
								}
							} catch (err) {
							}
							*/
							const currentDeck = addBuildCards(keepCards, index);
							setKeepCards([]);
							if (currentDeck == null) {
								return;
							}
							await updateCardData(currentDeck, true);
						}}
						addCard={async (index)=>{
							const currentDeck = addBuildCards(keepCards, index);
							setKeepCards([]);
							if (currentDeck == null) {
								return;
							}
							await updateCardData(currentDeck, true);
						}}
						cardSelector={cardSelector}
					/>
				</div>
			</div>
		</div>
	);
};
